import {
  Configuration,
  ConfigurationItem,
  ConfigurationAnswer
} from '@/models/configurator/configuration';

import { axios } from '@/plugins/axios';
import { AxiosRequestConfig } from 'axios';

export interface IConfigurationFilterParam extends Configuration {
  page?: number;
  search?: string;
}

export interface IConfigurationPaginatedListData {
  count: number;
  next: string | null;
  previous: string | null;
  results: Configuration[];
}

export class ConfigurationService {
  private endpoint = 'v1/configurator/configurations/';

  async fetchConfigurations(
    filter?: IConfigurationFilterParam
  ): Promise<IConfigurationPaginatedListData> {
    const config: AxiosRequestConfig | undefined = filter
      ? { params: filter }
      : undefined;
    return axios.get(`${this.endpoint}`, config).then(({ data }) => data);
  }

  async fetchConfiguration(id: number): Promise<Configuration> {
    return axios
      .get<Configuration>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  async fetchConfigurationAnswers(id: number): Promise<ConfigurationAnswer[]> {
    return axios
      .get<ConfigurationAnswer[]>(`${this.endpoint}${id}/answers/`)
      .then(({ data }) => data);
  }

  async createConfiguration(): Promise<Configuration> {
    return axios
      .post<Configuration>(`${this.endpoint}`, {})
      .then(({ data }) => data);
  }

  async updateConfiguration(
    configuration: Configuration
  ): Promise<Configuration> {
    return axios
      .patch<Configuration>(
        `${this.endpoint}${configuration.id}/`,
        configuration
      )
      .then(({ data }) => data);
  }

  async deleteConfiguration(id: number): Promise<any> {
    return axios.delete(`${this.endpoint}${id}/`);
  }

  async fetchConfigurationItems(id: number): Promise<ConfigurationItem[]> {
    return axios
      .get<ConfigurationItem[]>(`${this.endpoint}${id}/items/`)
      .then(({ data }) => data);
  }

  async fetchConfigurationItem(
    configurationId: number,
    id: number
  ): Promise<ConfigurationItem[]> {
    return axios
      .get<ConfigurationItem[]>(
        `${this.endpoint}${configurationId}/items/${id}/`
      )
      .then(({ data }) => data);
  }

  async createConfigurationItem(
    payload: ConfigurationItem
  ): Promise<ConfigurationItem> {
    return axios
      .post<ConfigurationItem>(
        `${this.endpoint}${payload.configuration}/items/`,
        payload
      )
      .then(({ data }) => data);
  }

  async updateConfigurationItem(
    payload: ConfigurationItem
  ): Promise<ConfigurationItem> {
    return axios
      .patch<ConfigurationItem>(
        `${this.endpoint}${payload.configuration}/items/${payload.id}/`,
        payload
      )
      .then(({ data }) => data);
  }

  async deleteConfigurationItem(
    payload: ConfigurationItem
  ): Promise<ConfigurationItem> {
    return axios
      .delete(`${this.endpoint}${payload.configuration}/items/${payload.id}/`)
      .then(({ data }) => data);
  }

  async uploadAfterMarketPartsItems(
    configurationId: number,
    file: File,
    snapshot?: number
  ): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    return axios
      .post<void>(
        `${this.endpoint}${configurationId}/items/upload_file/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 1000 * 60 * 5, //5mins,
          params: {
            pricing: snapshot
          }
        }
      )
      .then(({ data }) => data);
  }
}
